
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface AdminPractitioner {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  professional_title: string | null;
  bio: string | null;
  avatar_url: string | null;
  languages_spoken: string[] | null;
  session_formats_offered: string[] | null;
  in_person_address: string | null;
  latitude: number | null;
  longitude: number | null;
  average_rating: number | null;
  total_reviews: number;
  accepting_new_clients: boolean;
  is_verified: boolean;
  profile_status: string;
  years_of_experience: number | null;
  country: string | null;
  timezone: string;
  created_at: string;
  updated_at: string;
  education?: string;
  phone_number?: string;
  gender?: string;
  date_of_birth?: string;
  stripe_account_id?: string;
  payout_details_status?: string;
  admin_notes?: string;
  verification_notes?: string;
  services?: any[];
  categories?: any[];
}

export const usePractitioners = () => {
  const [practitioners, setPractitioners] = useState<AdminPractitioner[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const { toast } = useToast();
  
  const pageSize = 20;

  const fetchPractitioners = async () => {
    try {
      setLoading(true);
      
      // Call the Edge Function to get practitioners with email data
      const { data, error } = await supabase.functions.invoke('get-admin-practitioners', {
        body: {
          page,
          pageSize,
          searchQuery,
          statusFilter
        }
      });

      if (error) {
        console.error('Practitioners Error Details:', error);
        throw new Error(`Failed to fetch practitioners: ${error.message || error}`);
      }

      setPractitioners(data.practitioners || []);
      setTotalCount(data.count || 0);
    } catch (error) {
      console.error('Error fetching practitioners:', error);
      toast({
        variant: "destructive",
        title: "Error fetching practitioners",
        description: `There was an error loading the practitioners list. Error: ${error.message || error}`
      });
    } finally {
      setLoading(false);
    }
  };

  const updatePractitionerStatus = async (practitionerId: string, status: string) => {
    try {
      const { error } = await supabase
        .from('practitioners')
        .update({ profile_status: status, updated_at: new Date().toISOString() })
        .eq('id', practitionerId);

      if (error) throw error;

      toast({
        title: "Status updated",
        description: `Practitioner status has been updated to ${status}.`
      });

      fetchPractitioners();
    } catch (error) {
      console.error('Error updating practitioner status:', error);
      toast({
        variant: "destructive",
        title: "Error updating status",
        description: "There was an error updating the practitioner status."
      });
    }
  };

  const updateVerificationStatus = async (practitionerId: string, isVerified: boolean) => {
    try {
      const { error } = await supabase
        .from('practitioners')
        .update({ is_verified: isVerified, updated_at: new Date().toISOString() })
        .eq('id', practitionerId);

      if (error) throw error;

      toast({
        title: "Verification updated",
        description: `Practitioner ${isVerified ? 'verified' : 'unverified'} successfully.`
      });

      fetchPractitioners();
    } catch (error) {
      console.error('Error updating verification status:', error);
      toast({
        variant: "destructive",
        title: "Error updating verification",
        description: "There was an error updating the verification status."
      });
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  useEffect(() => {
    fetchPractitioners();
  }, [page, searchQuery, statusFilter]);

  return {
    practitioners,
    loading,
    page,
    setPage,
    totalCount,
    pageSize,
    searchQuery,
    setSearchQuery,
    statusFilter,
    setStatusFilter,
    updatePractitionerStatus,
    updateVerificationStatus,
    formatDate,
  };
};
